<template>
  <div v-if="challenge" class="px-2">
    <HeaderTitle single :back-target-func="backToChallenge">
      <template #bottom>{{ challenge.title }}</template>
    </HeaderTitle>
    <div v-if="isCurrentUserInAnyTeam" class="md:text-left w-full">
      <GoalElement
        :progress-text="t('custom.challenge.target.label')"
        :target-value="t('comp.challenge.started.target.label')"
      />
      <ProgressCircleElement
        :is-pass-fail-challenge="false"
        :is-team-challenge="true"
        :is-client-challenge="false"
        :average-progress-of-current-user="
          challenge.averageProgressOfCurrentUser
        "
        :progress="challenge.averageProgressOfCurrentUser"
        :competitive-challenge="challenge"
        :all-users-progress-sum="
          challenge.competitiveChallengeTeams.find(
            (team) => team.isTeamOfCurrentUser
          )?.averageProgress
        "
      >
      </ProgressCircleElement>
      <ChallengeDetailTile
        :end-date="challenge.endDate"
        :start-date="challenge.startDate"
        :image="challenge.image"
        :title="challenge.title"
      />

      <TabNavigation v-model:current-tab="currentTab" />

      <ContentSection
        v-if="currentTab === CompetitiveChallengeTabType.TAB_INFO"
        :content-sections="challenge.contentSections"
      />

      <TeamElement
        v-else-if="currentTab === CompetitiveChallengeTabType.TAB_TEAM"
        :team="userTeam"
        :challenge-type="challenge.competitiveChallengeType"
        :ranking-sort-type="challenge.rankingSortType"
        :team-id="userTeamId"
        :team-name="userTeamName"
        :team-total-progress="userTeamTotalProgress"
        :team-total-co2-saving="userTeamTotalCo2Saving"
        @refresh-team="fetchTeamMembers"
      />

      <RankingElement
        v-else-if="currentTab === CompetitiveChallengeTabType.TAB_RANKING"
        :challenge-type="challenge.competitiveChallengeType"
        :ranking-sort-type="challenge.rankingSortType"
        :progress="challenge.totalProgress"
        :co2progress="challenge.co2Saving"
        :teams="challenge?.competitiveChallengeTeams || []"
      />
    </div>
    <div v-else>
      <ChallengeDetailTile
        :end-date="challenge.endDate"
        :start-date="challenge.startDate"
        :image="challenge.image"
        :title="challenge.title"
      />

      <ContentSection :content-sections="challenge.contentSections" />
    </div>
    <div
      v-if="
        isCurrentUserInAnyTeam &&
        currentTab === CompetitiveChallengeTabType.TAB_TEAM
      "
      class="mt-16 text-center"
    >
      <BaseButton @click="leaveTeamModalOpen = true">{{
        t("comp.challenge.started.tab.team.leave.team.button.caption")
      }}</BaseButton>
    </div>

    <FloatingButton
      v-if="
        new Date(challenge.startDate) <= new Date() &&
        new Date(challenge.endDate) >= new Date() &&
        !isCurrentUserInAnyTeam
      "
      :label="t('comp.challenge.detail.join.team')"
      :icon="ChallengeStartIcon"
      button-type="white-inner"
      :fixed="true"
      @click="joinTeam()"
    />
    <FloatingButton
      v-else-if="new Date(challenge.startDate) >= new Date()"
      :fixed="true"
      disabled
      button-type="normal"
      :label="
        t('custom.challenge.start.from.button.caption', {
          s: DateTime.fromISO(challenge.startDate, { locale: 'de' }).toFormat(
            'LLLL yyyy'
          ),
        })
      "
      :icon="ChallengeStartIcon"
    />
    <FloatingButtonGroup
      v-if="isCurrentUserInAnyTeam"
      :competitive-challenge-id="challenge.mappingId"
      :end-date="challenge.endDate"
      :on-progress-update="updateChallengeProgress"
    />
    <EditHistoryModal
      v-if="progressModalOpen"
      :id="challenge.mappingId"
      :open="progressModalOpen"
      :is-pass-fail-challenge="false"
      :hint="challenge.hint"
      :date="new Date().toISOString()"
      :initial-value="getProgressForCurrentInterval()"
      :placeholder="challenge.placeholder"
      :is-competitive-challenge="true"
      :progress-value-type="progressValueType"
      @close="progressModalOpen = false"
      @update-history="redirectToStatistics"
    />
    <LeaveTeamModalOpen
      :open="leaveTeamModalOpen"
      :challenge-id="challenge.mappingId"
      :team-id="
        challenge.competitiveChallengeTeams.find(
          (team) => team.isTeamOfCurrentUser
        )?.id
      "
      @close="leaveTeamModalOpen = false"
    />
  </div>
</template>
<script setup lang="ts">
import HeaderTitle from "../../../components/v2/layout/header/HeaderTitle.vue";

import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import { NAVIGATION_CATEGORY_CHALLENGES } from "../../../enum/v2/navigationCategoryType";
import { useNavbarStore } from "../../../store/v2/navbar";
import ChallengeDetailTile from "../../../components/v2/challanges/ChallengeDetailTile.vue";
import ContentSection from "../../../components/v2/contentSections/ContentSection.vue";
import { CompetitiveChallengeDetailsDto } from "../../../dto/competitive-challenge-details.dto.ts";
import FloatingButton from "../../../components/v2/layout/button/FloatingButton.vue";
import ChallengeStartIcon from "../../../components/v2/icon/ChallengeStartIcon.vue";
import { useUserStore } from "../../../store/v2/user";
import { useClientStore } from "../../../store/v2/client";
import {
  fetchCompetitiveChallenge,
  fetchMembers,
} from "../../../api/v2/competitive-challenge.ts";
import FloatingButtonGroup from "../../../components/v2/layout/button/FloatingButtonGroup.vue";
import { DateTime } from "luxon";
import EditHistoryModal from "../../../components/v2/challanges/EditHistoryModal.vue";
import LeaveTeamModalOpen from "../../../components/v2/competitiveChallenge/LeaveTeamModal.vue";
import BaseButton from "../../../components/v2/layout/button/BaseButton.vue";
import GoalElement from "../../../components/v2/challanges/GoalElement.vue";
import ProgressCircleElement from "../../../components/v2/challanges/ProgressCircleElement.vue";
import TabNavigation from "../../../components/v2/competitiveChallenge/TabNavigation.vue";
import { CompetitiveChallengeTabType } from "../../../enum/v2/competitive-challenge-tab-type";
import RankingElement from "../../../components/v2/competitiveChallenge/RankingElement.vue";
import TeamElement from "../../../components/v2/competitiveChallenge/TeamElement.vue";
import { CompetitiveChallengeType } from "../../../enum/v2/competitive-challenge-type.ts";
import { ProgressValueType } from "../../../enum/v2/progress-value-type.ts";
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const user = useUserStore();
const client = useClientStore();
const navbarStore = useNavbarStore();
const userStore = useUserStore();

const challenge = ref<CompetitiveChallengeDetailsDto>(null);
const isCurrentUserInAnyTeam = ref(false);
const progressModalOpen = ref(false);
const leaveTeamModalOpen = ref(false);
const currentTab = ref<CompetitiveChallengeTabType>(
  CompetitiveChallengeTabType.TAB_TEAM
);
const userTeam = ref([]);
const userTeamId = ref(0);
const userTeamName = ref("");
const userTeamTotalProgress = ref(0);
const userTeamTotalCo2Saving = ref(0);
const progressValueType = computed<ProgressValueType | undefined>(() => {
  if (!challenge.value) {
    return undefined;
  }

  if (
    challenge.value.competitiveChallengeType ===
    CompetitiveChallengeType.COMPETITIVE_CHALLENGE_TYPE_STEPS
  ) {
    return ProgressValueType.Steps;
  }

  if (
    challenge.value.competitiveChallengeType ===
    CompetitiveChallengeType.COMPETITIVE_CHALLENGE_TYPE_BICYCLE
  ) {
    return ProgressValueType.Kilometers;
  }

  return undefined;
});

onBeforeMount(() => {
  navbarStore.setActiveNavigationCategory(NAVIGATION_CATEGORY_CHALLENGES);
});

onMounted(async () => {
  const challengeId = Number(route.params.challengeId as string);
  const res = await fetchCompetitiveChallenge(
    user.externalUserId,
    client.code,
    String(challengeId)
  );

  if (res.data) {
    challenge.value = res.data;
  } else {
    challenge.value = {} as CompetitiveChallengeDetailsDto;
  }

  isCurrentUserInAnyTeam.value = challenge.value.competitiveChallengeTeams.some(
    (team) => team.isTeamOfCurrentUser === true
  );

  if (isCurrentUserInAnyTeam.value) {
    await fetchTeamMembers();
  }
});

watch(
  () => route.params.challengeId,
  async (newId, oldId) => {
    if (newId !== oldId) {
      const challengeIdStr = Array.isArray(newId) ? newId[0] : String(newId);
      const res = await fetchCompetitiveChallenge(
        user.externalUserId,
        client.code,
        challengeIdStr
      );
      if (res.data) {
        challenge.value = res.data;
      } else {
        challenge.value = {} as CompetitiveChallengeDetailsDto;
      }
    }
  }
);

async function fetchTeamMembers() {
  const teamData = challenge.value.competitiveChallengeTeams.find(
    (team) => team.isTeamOfCurrentUser
  );

  userTeamId.value = teamData.id;
  userTeamName.value = teamData.name;
  userTeamTotalProgress.value = teamData.totalProgress;
  userTeamTotalCo2Saving.value = teamData.totalCo2Saving;

  const resTeam = await fetchMembers(userStore.externalUserId, teamData.id);
  if (resTeam.data) {
    userTeam.value = resTeam.data;
  }
}

function joinTeam() {
  router.push({
    name: "competitiveChallengeJoinTeam",
    params: { challengeId: challenge.value.mappingId },
  });
}

async function updateChallengeProgress() {
  progressModalOpen.value = true;
}

async function redirectToStatistics() {
  router.push({
    name: "competitiveChallengeStatistics",
    params: { challengeId: challenge.value.mappingId },
  });
}

function getProgressForCurrentInterval() {
  const foundProgress = challenge.value.progressesOfCurrentUser.find(
    (progress) => {
      return (
        new Date(progress.date).toDateString() == new Date().toDateString()
      );
    }
  );

  return foundProgress ? foundProgress.value : "";
}

function backToChallenge() {
  if (window.history.state) {
    window.history.back();
  } else {
    router.push({
      name: "challenges",
    });
  }
}
</script>
